import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { FaUserCircle, FaSignOutAlt, FaChevronDown, FaUser } from 'react-icons/fa';
import ForgotPassword from "./pages/ForgotPassword";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Dashboard from './components/Dashboard';
import AddAsset from './components/AddAsset';
import ViewAssets from './components/ViewAssets';
import MaintenanceHub from './components/MaintenanceHub';
import LabTechnicianDashboard from './components/LabTechnicianDashboard';
import TechnicianProfile from './components/TechnicianProfile';
import ResetPassword from "./pages/ResetPassword";
import Signup from './components/Signup';
import Login from './components/Login';
import BloggerLogin from './components/BloggerLogin';
import authService from './services/authService';
import LabAdminDashboard from './pages/LabAdminDashboard';
import AssistantHomeDashboard from './components/AssistantHomeDashboard';
import PricingPage from './components/landing-page/PricingPage';
import WhyAssetflowPage from './components/landing-page/WhyAssetflowPage';
import HomePage from './components/HomePage';
import IssuesPage from './pages/IssuesPage';
import CreateIssuePage from './pages/CreateIssuePage';
import AssetUserDashboard from './components/AssetUserDashboard';
import AssetRequestForm from './components/AssetRequestForm';
import AssetManagement from './components/AssetManagement';
import AssetUserRequestPending from './pages/AssetUserRequestPending';
import AssetUserApprovalPage from './pages/AssetUserApprovalPage';
import AssetHistoryPage from './pages/AssetHistoryPage';
import AssetUserHistoryPage from './pages/AssetUserHistoryPage';
import ManageAssetUsersPage from './pages/ManageAssetUsersPage';
import AdminBuyPlan from './pages/AdminBuyPlan';
import UserProfile from './components/UserProfile';
import NotificationBell from './components/NotificationBell';
import NotificationsPage from './pages/NotificationsPage';
import BreakdownRequests from './components/BreakdownRequests';
import ExtensionReleaseRequests from './components/ExtensionReleaseRequests';
import TechnicianMaintenanceRequestsPage from './components/TechnicianMaintenanceRequestsPage';
import TechnicianIssues from './components/TechnicianIssues';
import GenerateBarcode from './components/GenerateBarcode';
import BarcodeDetailsPage from './pages/BarcodeDetailsPage';
import CompliancePage from './pages/CompliancePage';
import SuperAdminLogin from './components/SuperAdminLogin';
import SuperAdminDashboard from './pages/SuperAdmin/SuperAdminDashboard';
import PlansManagement from './pages/SuperAdmin/PlansManagement';
import CompaniesManagement from './pages/SuperAdmin/CompaniesManagement';
import SubscriptionsManagement from './pages/SuperAdmin/SubscriptionsManagement';
import TrialUsersManagement from './pages/SuperAdmin/TrialUsersManagement';
import PaymentGatewayPlaceholder from './pages/PaymentGatewayPlaceholder';
import AdminFaqChatbot from './chatbot/AdminFaqChatbot';
import SupplierDetails from './pages/SupplierDetails';
import FeaturesPage from './components/landing-page/FeaturesPage';
import BlogPage from './components/landing-page/BlogsPage';
import ContactPage from './components/landing-page/ContactPage';
import IndustryInfoPage from './components/landing-page/IndustryInfoPage';
import CreateBlog from './components/SuperAdmin/CreateBlog';
import BlogList from './components/SuperAdmin/BlogList';
import BlogDetail from './components/SuperAdmin/BlogDetail';
import BlogDetailPage from './components/landing-page/BlogDetailPage';
import BloggerDashboard from './pages/BloggerDashboard';
import './styles/App.css';

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole, userRole, isLoggedIn }) => {
  if (!isLoggedIn) {
    return <Navigate to="/login" />;
  }

  if (requiredRole && userRole !== requiredRole) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function App() {
  const [, setActiveTab] = useState('dashboard');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    // Check if user is already logged in
    const loggedInUser = authService.getCurrentUser();
    if (loggedInUser) {
      setUser(loggedInUser);
      setIsLoggedIn(true);
    }
    setLoading(false);
  }, []);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignupSuccess = (userData) => {
    setUser(userData);
    setIsLoggedIn(true);
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsLoggedIn(true);
    // Set active tab based on role so navbar highlights correctly after login
    if (userData?.role === 'admin') setActiveTab('admin');
    else if (userData?.role === 'asset_technician') setActiveTab('profile');
    else if (userData?.role === 'asset_user') setActiveTab('asset-user');
    else setActiveTab('dashboard');
  };

  const handleLogout = () => {
    authService.logout();
    setUser(null);
    setIsLoggedIn(false);
    window.location.href = '/';
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const isLabAdmin = user?.role === 'admin';
  const isLabTechnician = user?.role === 'asset_technician';
  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
          <div className="App">
        {!isLoggedIn ? (
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/blogs" element={<BlogPage />} />
            <Route path="/blog/:slug/:id" element={<BlogDetailPage />} />
            <Route path="/blog/:id" element={<BlogDetailPage />} />
            <Route path="/why-assetflow" element={<WhyAssetflowPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/industry/:industryId" element={<IndustryInfoPage />} />
            <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
            <Route path="/blogger/login" element={<BloggerLogin onLoginSuccess={handleLoginSuccess} />} />
            <Route path="/super-admin-login" element={<SuperAdminLogin onLoginSuccess={handleLoginSuccess} />} />
            <Route path="/signup" element={<Signup onSignupSuccess={handleSignupSuccess} />} />
            <Route path="/forgot-password" element={<ForgotPassword/>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/asset-user-request-pending" element={<AssetUserRequestPending />} />
            <Route path="/barcode/:encodedPayload" element={<BarcodeDetailsPage />} />
            <Route path="/barcode" element={<BarcodeDetailsPage />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        ) : (
          <>
            <nav className="navbar">
              <div className="nav-top-row">
                <div className="logo">
                  <h1>{user?.role === 'asset_user' ? 'Asset User Dashboard' : user?.role === 'super_admin' ? 'Super Admin Panel' : 'Asset Management System'}</h1>
                  <p>{isSuperAdmin ? 'Control Center' : isLabAdmin ? 'Admin Portal' : isLabTechnician ? 'Technician Portal' : user?.role === 'asset_user' ? 'Asset user Portal' : user?.role === 'blogger' ? 'Blogger Portal' : 'Asset Incharge Portal'}</p>
                </div>

                <div className="navbar-right-section">
                  <NotificationBell user={user} />
                  <div className="user-profile-section" ref={profileRef}>
                  <div
                    className="profile-trigger"
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  >
                    {user?.profilePic ? (
                      <img src={user.profilePic} alt="Profile" className="profile-avatar-img" />
                    ) : (
                      <FaUserCircle className="profile-avatar" />
                    )}
                    <div className="profile-trigger-info">
                      <span className="profile-name">{user?.name}</span>
                      <span className="profile-role-label">{user?.role?.replace(/_/g, ' ')}</span>
                    </div>
                    <FaChevronDown className={`profile-chevron ${showProfileDropdown ? 'open' : ''}`} />
                  </div>

                  {showProfileDropdown && (
                    <div className="profile-dropdown">
                      <div className="profile-dropdown-header">
                        {user?.profilePic ? (
                          <img src={user.profilePic} alt="Profile" className="dropdown-avatar-img" />
                        ) : (
                          <FaUserCircle className="dropdown-avatar" />
                        )}
                        <div className="dropdown-user-details">
                          <span className="dropdown-name">{user?.name}</span>
                          <span className="dropdown-email">{user?.email}</span>
                        </div>
                      </div>
                      <div className="dropdown-role">
                        <span className={`role-badge role-${user?.role}`}>
                          {user?.role?.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="dropdown-divider" />
                      <Link
                        to="/my-profile"
                        className="dropdown-profile-link"
                        onClick={() => setShowProfileDropdown(false)}
                      >
                        <FaUser /> My Profile
                      </Link>
                      <div className="dropdown-divider" />
                      <button className="dropdown-logout-btn" onClick={handleLogout}>
                        <FaSignOutAlt /> Logout
                      </button>
                    </div>
                  )}
                </div>
                </div>
              </div>

              <div className="nav-bottom-row">
                <ul className="nav-menu">
                 

                  

                

           
                </ul>
              </div>
            </nav>

            <main className="main-content">
              <Routes>
               
                {/* Default Route - based on role */}
                <Route
                  path="/"
                  element={
                    // If admin is logged in, show admin dashboard at root
                    isLoggedIn && user?.role === 'super_admin' ? (
                      <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="super_admin" userRole={user?.role}>
                        <SuperAdminDashboard user={user} />
                      </ProtectedRoute>
                    ) : isLoggedIn && user?.role === 'blogger' ? (
                      <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="blogger" userRole={user?.role}>
                        <BloggerDashboard user={user} />
                      </ProtectedRoute>
                    ) : isLoggedIn && user?.role === 'admin' ? (
                      <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="admin" userRole={user?.role}>
                        <LabAdminDashboard user={user} />
                      </ProtectedRoute>
                    ) : isLoggedIn && user?.role === 'asset_technician' ? (
                      <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="asset_technician" userRole={user?.role}>
                        <LabTechnicianDashboard user={user} />
                      </ProtectedRoute>
                    ) : isLoggedIn && user?.role === 'asset_user' ? (
                      <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="asset_user" userRole={user?.role}>
                        <AssetUserDashboard user={user} />
                      </ProtectedRoute>
                    ) : (
                      <ProtectedRoute isLoggedIn={isLoggedIn} requiredRole="asset_incharge" userRole={user?.role}>
                        <AssistantHomeDashboard user={user} />
                      </ProtectedRoute>
                    )
                  }
                />
                <Route
                  path="/issues"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn} userRole={user?.role}>
                      <IssuesPage user={user} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/issues/create"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn} userRole={user?.role}>
                      <CreateIssuePage user={user} />
                    </ProtectedRoute>
                  }
                />

                {/* Asset User Routes */}
                <Route
                  path="/asset-user-dashboard"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="asset_user"
                      userRole={user?.role}
                    >
                      <AssetUserDashboard user={user} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/asset-request"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="asset_user"
                      userRole={user?.role}
                    >
                      <AssetRequestForm user={user} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/asset-user-history"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="asset_user"
                      userRole={user?.role}
                    >
                      <AssetUserHistoryPage user={user} />
                    </ProtectedRoute>
                  }
                />

                {/* Asset Incharge Asset Management */}
                <Route
                  path="/asset-management"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="asset_incharge"
                      userRole={user?.role}
                    >
                      <AssetManagement user={user} />
                    </ProtectedRoute>
                  }
                />

                {/* Asset Incharge Manage Users */}
                <Route
                  path="/manage-asset-users"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="asset_incharge"
                      userRole={user?.role}
                    >
                      <ManageAssetUsersPage user={user} />
                    </ProtectedRoute>
                  }
                />

                {/* Asset Incharge Manage Suppliers */}
                <Route
                  path="/manage-suppliers"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="asset_incharge"
                      userRole={user?.role}
                    >
                      <SupplierDetails user={user} />
                    </ProtectedRoute>
                  }
                />

                {/* Asset Incharge Routes */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute 
                      isLoggedIn={isLoggedIn} 
                      requiredRole="asset_incharge"
                      userRole={user?.role}
                    >
                      <Dashboard user={user} />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/add-asset" 
                  element={
                    <ProtectedRoute 
                      isLoggedIn={isLoggedIn} 
                      requiredRole="asset_incharge"
                      userRole={user?.role}
                    >
                      <AddAsset user={user} />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/assets" 
                  element={
                    <ProtectedRoute 
                      isLoggedIn={isLoggedIn} 
                      requiredRole="asset_incharge"
                      userRole={user?.role}
                    >
                      <ViewAssets user={user} />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/maintenance" 
                  element={
                    <ProtectedRoute 
                      isLoggedIn={isLoggedIn} 
                      requiredRole="asset_incharge"
                      userRole={user?.role}
                    >
                      <MaintenanceHub user={user} />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/generate-barcode"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="asset_incharge"
                      userRole={user?.role}
                    >
                      <GenerateBarcode />
                    </ProtectedRoute>
                  }
                />
                <Route path="/barcode/:encodedPayload" element={<BarcodeDetailsPage />} />
                <Route path="/barcode" element={<BarcodeDetailsPage />} />

                {/* Lab Assistant Dashboard Route */}
                <Route path="/breakdown-requests" element={<BreakdownRequests />} />
<Route path="/extension-requests" element={<ExtensionReleaseRequests />} />

<Route path="/add-asset" element={<AddAsset />} />
<Route path="/asset-history/:assetId" element={<AssetHistoryPage />} />

                

                {/* Lab Technician Routes */}
                <Route 
                  path="/technician-dashboard" 
                  element={
                    <ProtectedRoute 
                      isLoggedIn={isLoggedIn} 
                      requiredRole="asset_technician"
                      userRole={user?.role}
                    >
                      <LabTechnicianDashboard user={user} />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/lab-dashboard" 
                  element={
                    <ProtectedRoute 
                      isLoggedIn={isLoggedIn} 
                      requiredRole="asset_technician"
                      userRole={user?.role}
                    >
                      <LabTechnicianDashboard user={user} />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/lab-dashboard/issues" 
                  element={
                    <ProtectedRoute 
                      isLoggedIn={isLoggedIn} 
                      requiredRole="asset_technician"
                      userRole={user?.role}
                    >
                      <TechnicianIssues user={user} />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute 
                      isLoggedIn={isLoggedIn} 
                      requiredRole="asset_technician"
                      userRole={user?.role}
                    >
                      <TechnicianProfile user={user} onSkillsUpdate={setUser} />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/maintenance-requests" 
                  element={
                    <ProtectedRoute 
                      isLoggedIn={isLoggedIn} 
                      requiredRole="asset_technician"
                      userRole={user?.role}
                    >
                      <TechnicianMaintenanceRequestsPage user={user} />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/compliance-management" 
                  element={
                    <ProtectedRoute 
                      isLoggedIn={isLoggedIn} 
                      requiredRole="asset_technician"
                      userRole={user?.role}
                    >
                      <CompliancePage user={user} />
                    </ProtectedRoute>
                  } 
                />

                {/* Lab Admin Routes */}
                <Route 
                  path="/lab-admin/dashboard" 
                  element={
                    <ProtectedRoute 
                      isLoggedIn={isLoggedIn} 
                      requiredRole="admin"
                      userRole={user?.role}
                    >
                      <LabAdminDashboard user={user} />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/asset-user-approvals" 
                  element={
                    <ProtectedRoute 
                      isLoggedIn={isLoggedIn} 
                      requiredRole="admin"
                      userRole={user?.role}
                    >
                      <AssetUserApprovalPage user={user} />
                    </ProtectedRoute>
                  } 
                />

                <Route
                  path="/admin/buy-plan"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="admin"
                      userRole={user?.role}
                    >
                      <AdminBuyPlan />
                    </ProtectedRoute>
                  }
                />

                {/* Notifications Page - All roles */}
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn} userRole={user?.role}>
                      <NotificationsPage user={user} />
                    </ProtectedRoute>
                  }
                />

                {/* User Profile Route - All roles */}
                <Route
                  path="/my-profile"
                  element={
                    <ProtectedRoute isLoggedIn={isLoggedIn} userRole={user?.role}>
                      <UserProfile user={user} onProfileUpdate={setUser} />
                    </ProtectedRoute>
                  }
                />

                {/* Super Admin Routes */}
                <Route
                  path="/super-admin/dashboard"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="super_admin"
                      userRole={user?.role}
                    >
                      <SuperAdminDashboard user={user} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/super-admin/plans"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="super_admin"
                      userRole={user?.role}
                    >
                      <PlansManagement user={user} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/super-admin/companies"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="super_admin"
                      userRole={user?.role}
                    >
                      <CompaniesManagement user={user} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/super-admin/subscriptions"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="super_admin"
                      userRole={user?.role}
                    >
                      <SubscriptionsManagement user={user} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/super-admin/trial-users"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="super_admin"
                      userRole={user?.role}
                    >
                      <TrialUsersManagement user={user} />
                    </ProtectedRoute>
                  }
                />

                {/* Blog Management Routes */}
                <Route
                  path="/super-admin/blogs"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="super_admin"
                      userRole={user?.role}
                    >
                      <BlogList user={user} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/super-admin/blogs/create"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="super_admin"
                      userRole={user?.role}
                    >
                      <CreateBlog user={user} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/super-admin/blogs/edit/:id"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="super_admin"
                      userRole={user?.role}
                    >
                      <CreateBlog user={user} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/super-admin/blogs/:id"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="super_admin"
                      userRole={user?.role}
                    >
                      <BlogDetail user={user} />
                    </ProtectedRoute>
                  }
                />

                {/* Blogger Routes */}
                <Route
                  path="/blogger/dashboard"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="blogger"
                      userRole={user?.role}
                    >
                      <BloggerDashboard user={user} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/blogger/blogs"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="blogger"
                      userRole={user?.role}
                    >
                      <BlogList user={user} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/blogger/blogs/create"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="blogger"
                      userRole={user?.role}
                    >
                      <CreateBlog user={user} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/blogger/blogs/edit/:id"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="blogger"
                      userRole={user?.role}
                    >
                      <CreateBlog user={user} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/blogger/blogs/:id"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="blogger"
                      userRole={user?.role}
                    >
                      <BlogDetail user={user} />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/payment-gateway"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="admin"
                      userRole={user?.role}
                    >
                      <PaymentGatewayPlaceholder />
                    </ProtectedRoute>
                  }
                />

                {/* Blogger Routes */}
                <Route
                  path="/blogger/dashboard"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="blogger"
                      userRole={user?.role}
                    >
                      <BloggerDashboard user={user} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/blogger/blogs"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="blogger"
                      userRole={user?.role}
                    >
                      <BlogList user={user} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/blogger/blogs/create"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="blogger"
                      userRole={user?.role}
                    >
                      <CreateBlog user={user} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/blogger/blogs/edit/:id"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="blogger"
                      userRole={user?.role}
                    >
                      <CreateBlog user={user} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/blogger/blogs/:id"
                  element={
                    <ProtectedRoute
                      isLoggedIn={isLoggedIn}
                      requiredRole="blogger"
                      userRole={user?.role}
                    >
                      <BlogDetail user={user} />
                    </ProtectedRoute>
                  }
                />

                {/* Redirect unknown routes */}
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </main>

            <AdminFaqChatbot
              isLoggedIn={isLoggedIn}
              userRole={user?.role}
              userName={user?.name}
            />

            <footer className="footer">
              <p>&copy; 2026 Asset Management System. All rights reserved.</p>
            </footer>
          </>
        )}
      </div>
      
    </Router>
  );
}

export default App;
